import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import XJSJScene from './xjsj-scene';
import { PerspectiveCamera, ShaderMaterial, Vector2 } from 'three';
import localCloudFrag from '../shaders/composer/localCloudFrag';
import localCloudVert from '../shaders/composer/localCloudVert';
import blurFrag from '../shaders/composer/blurFrag';
import blurVert from '../shaders/composer/blurVert';
import gsap from 'gsap';
import emitter, {
  EVENT_NAME_CHANGE_STATE,
  EVENT_NAME_CHANGE_STATE_FINISH,
  EVENT_NAME_CHANGE_STATE_START,
} from '../utils/mitt';
import texManager from './tex-manager';
export class Composer {
  xjsjScene: XJSJScene;
  renderPass!: RenderPass;
  localCloudPassVUniforms!: { [k: string]: { value: any } };
  localCloudPass!: ShaderPass;
  blurVPass!: ShaderPass;
  blurPassVUniforms!: { [k: string]: { value: any } };
  blurHPass!: ShaderPass;
  blurPassHUniforms!: { [k: string]: { value: any } };
  finalComposer!: EffectComposer;
  isBlur = false;
  isShowCloud = false;
  blurTween?: gsap.core.Tween;
  cloudTween?: gsap.core.Timeline;

  constructor(xjsjScene: XJSJScene) {
    this.xjsjScene = xjsjScene;
    this.renderPass = new RenderPass(xjsjScene.scene, xjsjScene.camera);
    // 局部云pass
    this.localCloudPassVUniforms = {
      tDiffuse: { value: null },
      tCloud: { value: texManager.get('localCloud') },
      tNoise: { value: texManager.get('noise') },
      resolution: {
        value: new Vector2(xjsjScene.width, xjsjScene.height).multiplyScalar(
          xjsjScene.pixelRatio
        ),
      },
      time: { value: 0 },
      showFactor: { value: 1 },
      opacity: { value: 0 },
    };
    this.localCloudPass = new ShaderPass(
      new ShaderMaterial({
        uniforms: this.localCloudPassVUniforms,
        vertexShader: localCloudVert,
        fragmentShader: localCloudFrag,
      }),
      'tDiffuse'
    );
    // 模糊pass
    this.blurPassVUniforms = {
      tDiffuse: { value: null },
      resolution: {
        value: new Vector2(xjsjScene.width, xjsjScene.height).multiplyScalar(
          xjsjScene.pixelRatio
        ),
      },
      direction: { value: new Vector2(0, 1) },
      center: { value: new Vector2(0.35, 0.5) },
      blurFactor: { value: 0 },
    };
    this.blurVPass = new ShaderPass(
      new ShaderMaterial({
        uniforms: this.blurPassVUniforms,
        vertexShader: blurVert,
        fragmentShader: blurFrag,
      }),
      'tDiffuse'
    );
    this.blurPassHUniforms = {
      tDiffuse: { value: null },
      resolution: {
        value: new Vector2(xjsjScene.width, xjsjScene.height).multiplyScalar(
          xjsjScene.pixelRatio
        ),
      },
      direction: { value: new Vector2(1, 0) },
      center: { value: new Vector2(0.35, 0.5) },
      blurFactor: { value: 0 },
    };
    this.blurHPass = new ShaderPass(
      new ShaderMaterial({
        uniforms: this.blurPassHUniforms,
        vertexShader: blurVert,
        fragmentShader: blurFrag,
      }),
      'tDiffuse'
    );

    this.finalComposer = new EffectComposer(xjsjScene.renderer);
    this.finalComposer.setPixelRatio(xjsjScene.pixelRatio);
    this.finalComposer.renderToScreen = true;
    this.finalComposer.addPass(this.renderPass);
    this.finalComposer.addPass(this.blurVPass);
    this.finalComposer.addPass(this.blurHPass);
    this.finalComposer.addPass(this.blurVPass);
    this.finalComposer.addPass(this.blurHPass);
    this.finalComposer.addPass(this.blurVPass);
    this.finalComposer.addPass(this.blurHPass);
    this.finalComposer.addPass(this.localCloudPass);

    emitter.on(EVENT_NAME_CHANGE_STATE, (stateIndex: number) => {
      if (stateIndex === 0) {
        this.setBlur(false);
      } else {
        this.setBlur(true);
      }
    });
    emitter.on(EVENT_NAME_CHANGE_STATE_START, (stateIndex: number) => {
      this.setCloudShow(false, 1);
    });
    emitter.on(EVENT_NAME_CHANGE_STATE_FINISH, (stateIndex: number) => {
      if (stateIndex === 0) {
        this.setCloudShow(false, 1);
      } else {
        this.setCloudShow(true);
      }
    });
  }

  setBlur(isBlur: boolean) {
    if (this.isBlur === isBlur) return;
    this.isBlur = isBlur;
    if (this.blurTween) {
      this.blurTween.kill();
    }
    const animData = { f: this.blurPassHUniforms.blurFactor.value };
    this.blurTween = gsap.to(animData, {
      f: isBlur ? 1 : 0,
      duration: 1,
      onUpdate: () => {
        this.blurPassHUniforms.blurFactor.value = animData.f;
        this.blurPassVUniforms.blurFactor.value = animData.f;
      },
    });
  }

  setCloudShow(isShowCloud: boolean, duration = 1.5) {
    if (this.isShowCloud === isShowCloud) return;
    this.isShowCloud = isShowCloud;
    if (this.cloudTween) {
      this.cloudTween.kill();
    }
    // const animData = { f: this.localCloudPassVUniforms.showFactor.value };
    this.cloudTween = new gsap.core.Timeline().to(
      this.localCloudPassVUniforms.opacity,
      {
        value: isShowCloud ? 1 : 0,
        duration: duration,
      },
      '<'
    );
  }

  setSize(w: number, h: number) {
    this.finalComposer.setSize(w, h);
    this.blurPassHUniforms.resolution.value
      .set(w, h)
      .multiplyScalar(this.xjsjScene.pixelRatio);
    this.blurPassVUniforms.resolution.value
      .set(w, h)
      .multiplyScalar(this.xjsjScene.pixelRatio);
    this.localCloudPassVUniforms.resolution.value
      .set(w, h)
      .multiplyScalar(this.xjsjScene.pixelRatio);
  }

  setCamera(camera: PerspectiveCamera) {
    this.renderPass.camera = camera;
  }

  render() {
    this.localCloudPassVUniforms.time.value =
      this.xjsjScene.clock.getElapsedTime();
    this.finalComposer.render();
    // this.xjsjScene.renderer.render(this.xjsjScene.scene, this.xjsjScene.camera);
  }
}
